/**
 * PUBLIC test page (no login) — a customer opens this link on their phone and
 * records a video INSIDE the page: live camera preview, Record / Stop, watch
 * it back, then upload. Scaffold for a future module; the API is
 * nova-api testVideo.controller.js and the token is only a folder name.
 *
 * Recording is getUserMedia + MediaRecorder, which is what "record in the
 * browser" means: the page owns the camera and the file, so the flow is
 * one screen and works the same on a laptop as on a phone. The first cut
 * used <input type=file capture>, which only hands off to the phone's own
 * camera app and on a desktop is a plain file picker -- it never recorded
 * anything itself.
 *
 * Browser support is wide but not universal (older iOS, some webviews), so
 * when either API is missing the page falls back to that file input rather
 * than a dead button. The upload is the same either way.
 *
 * The stream is stopped the moment recording stops, and again on unmount, so
 * the camera light goes off while the customer watches the take back.
 */
import axios from "axios";
import { useCallback, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";

const MAX_SECONDS = 180;

// First type the browser can actually produce. Chrome/Firefox/Android take
// webm; Safari (iOS 14.3+) only mp4. An empty string lets the browser pick.
const pickMimeType = () => {
  if (typeof MediaRecorder === "undefined") return "";
  const candidates = [
    "video/webm;codecs=vp9,opus",
    "video/webm;codecs=vp8,opus",
    "video/webm",
    "video/mp4",
  ];
  return candidates.find((t) => MediaRecorder.isTypeSupported(t)) || "";
};

const extFor = (mime) => (String(mime).includes("mp4") ? "mp4" : "webm");

const fmt = (s) =>
  `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;

const canRecordInBrowser = () =>
  typeof navigator !== "undefined" &&
  !!navigator.mediaDevices?.getUserMedia &&
  typeof MediaRecorder !== "undefined";

// Browsers only expose the camera on a SECURE origin: https, or localhost.
// A link opened as http://10.0.3.123:3000 from a phone on the LAN has no
// navigator.mediaDevices at all -- not a missing feature, a withheld one --
// so the message has to say that, or "your browser cannot record" reads as
// a broken phone. Production is https and unaffected.
const insecureOrigin = () =>
  typeof window !== "undefined" && window.isSecureContext === false;

// Where to upload. REACT_APP_API_URI is the rule; the exception is a DEV
// build reached from another device: the env says localhost, which on the
// phone is the phone, so every upload was a "Network Error". When the page
// itself was loaded from a non-localhost host and the env points at
// localhost, the API is on that same host instead. Production URLs are
// never localhost, so this cannot fire there.
const apiBase = () => {
  const configured = process.env.REACT_APP_API_URI || "";
  try {
    const api = new URL(configured);
    const here = window.location.hostname;
    const local = (h) => h === "localhost" || h === "127.0.0.1";
    if (local(api.hostname) && !local(here)) {
      api.hostname = here;
      return api.origin;
    }
  } catch {
    /* not a URL -- use it as given */
  }
  return configured.replace(/\/$/, "");
};

export default function RecordVideoTest() {
  const { token } = useParams();

  // idle -> live -> recording -> review -> (upload) -> done
  const [phase, setPhase] = useState("idle");
  const [facing, setFacing] = useState("environment");
  const [canFlip, setCanFlip] = useState(false);
  const [seconds, setSeconds] = useState(0);
  const [blob, setBlob] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [status, setStatus] = useState(null); // {type, msg}
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [supported] = useState(canRecordInBrowser);

  const liveRef = useRef(null);
  const streamRef = useRef(null);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);
  const fileInputRef = useRef(null);

  const stopStream = useCallback(() => {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    if (liveRef.current) liveRef.current.srcObject = null;
  }, []);

  const stopTimer = () => {
    clearInterval(timerRef.current);
    timerRef.current = null;
  };

  // Object URL for the take; revoked when replaced or on unmount.
  useEffect(() => {
    if (!blob) {
      setPreviewUrl(null);
      return undefined;
    }
    const url = URL.createObjectURL(blob);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [blob]);

  useEffect(
    () => () => {
      stopTimer();
      stopStream();
    },
    [stopStream],
  );

  // Camera on. A user gesture is required on iOS, hence a button and not an
  // effect on mount.
  const startCamera = async (facingMode = facing) => {
    setStatus(null);
    stopStream();
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode, width: { ideal: 1280 }, height: { ideal: 720 } },
        audio: true,
      });
      streamRef.current = stream;
      if (liveRef.current) {
        liveRef.current.srcObject = stream;
        // play() can reject harmlessly if the element is not yet visible.
        liveRef.current.play().catch(() => {});
      }
      setPhase("live");
      try {
        const devices = await navigator.mediaDevices.enumerateDevices();
        setCanFlip(devices.filter((d) => d.kind === "videoinput").length > 1);
      } catch {
        setCanFlip(false);
      }
    } catch (err) {
      const denied = err?.name === "NotAllowedError" || err?.name === "SecurityError";
      setStatus({
        type: "error",
        msg: denied
          ? "Camera access was blocked. Allow the camera for this site and try again."
          : `Could not start the camera: ${err?.message || err?.name || "unknown error"}`,
      });
      setPhase("idle");
    }
  };

  const flipCamera = () => {
    const next = facing === "environment" ? "user" : "environment";
    setFacing(next);
    startCamera(next);
  };

  const startRecording = () => {
    const stream = streamRef.current;
    if (!stream) return;
    chunksRef.current = [];
    const mimeType = pickMimeType();
    let recorder;
    try {
      recorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
    } catch (err) {
      setStatus({ type: "error", msg: `Recording is not available here: ${err.message}` });
      return;
    }
    recorder.ondataavailable = (e) => {
      if (e.data && e.data.size) chunksRef.current.push(e.data);
    };
    recorder.onstop = () => {
      const type = recorder.mimeType || mimeType || "video/webm";
      setBlob(new Blob(chunksRef.current, { type }));
      // Camera off while they review; a retake asks again.
      stopStream();
      setPhase("review");
    };
    recorderRef.current = recorder;
    recorder.start(1000); // a chunk a second, so a crash mid-take loses ~1s
    setSeconds(0);
    setStatus(null);
    setPhase("recording");
    timerRef.current = setInterval(() => {
      setSeconds((s) => {
        if (s + 1 >= MAX_SECONDS) stopRecording();
        return s + 1;
      });
    }, 1000);
  };

  const stopRecording = () => {
    stopTimer();
    const r = recorderRef.current;
    if (r && r.state !== "inactive") r.stop();
  };

  const retake = () => {
    setBlob(null);
    setStatus(null);
    setProgress(0);
    startCamera();
  };

  const upload = async (file) => {
    if (!file) return;
    try {
      setUploading(true);
      setStatus(null);
      setProgress(0);
      const formData = new FormData();
      formData.append("file", file, file.name || `recording.${extFor(file.type)}`);
      formData.append("token", token);
      const { data } = await axios.post(
        `${apiBase()}/test-video/upload`,
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
          onUploadProgress: (e) => {
            if (e.total) setProgress(Math.round((e.loaded / e.total) * 100));
          },
        },
      );
      if (data?.success) {
        setStatus({ type: "success", msg: "Video uploaded. Thank you!" });
        setBlob(null);
        setPhase("done");
        if (fileInputRef.current) fileInputRef.current.value = "";
      } else {
        setStatus({ type: "error", msg: data?.message || "Upload failed" });
      }
    } catch (err) {
      setStatus({
        type: "error",
        msg: err?.response?.data?.message || err.message || "Upload failed",
      });
    } finally {
      setUploading(false);
    }
  };

  const uploadTake = () => {
    if (!blob) return;
    // The recorder reports "video/webm;codecs=vp9,opus". Send the bare media
    // type: the codec parameter is what the multipart parser on the other
    // side turned into "not a video", and an empty type (some phones) would
    // go out as application/octet-stream. The extension carries the format
    // either way.
    const bare = String(blob.type || "").split(";")[0].trim() || "video/webm";
    const ext = extFor(bare);
    upload(new File([blob], `recording.${ext}`, { type: bare }));
  };

  // Fallback path only: the phone's own camera app / a file picker.
  const onPickFile = (e) => {
    const picked = e.target.files?.[0] || null;
    setStatus(null);
    if (picked) setBlob(picked);
    setPhase(picked ? "review" : "idle");
  };

  const showLive = phase === "live" || phase === "recording";

  return (
    <div
      className="d-flex flex-column align-items-center"
      style={{ minHeight: "100vh", background: "#f5f6f8", padding: "24px 16px" }}
    >
      <div
        className="bg-white w-100"
        style={{ maxWidth: 520, borderRadius: 16, padding: 20, boxShadow: "0 2px 12px rgba(0,0,0,.08)" }}
      >
        <h4 className="mb-1">Record a Video</h4>
        <p className="text-muted small mb-3">
          {supported
            ? "Start the camera, record your video, watch it back, then upload it."
            : insecureOrigin()
              ? "This link was opened over plain http, so the browser will not open the camera in the page. Record with your camera app instead, then upload."
              : "Your browser cannot record in the page. Record with your camera app instead, then upload."}
        </p>

        {/* Live preview. Kept mounted across live/recording so srcObject
            survives the phase change; hidden otherwise. */}
        <div
          className="position-relative mb-3"
          style={{ display: showLive ? "block" : "none" }}
        >
          <video
            ref={liveRef}
            muted
            autoPlay
            playsInline
            className="w-100"
            // Capped: a phone camera is portrait, and at full height the
            // preview alone filled the screen and pushed Record below the
            // fold. Cover, so the box stays a fixed shape while the camera
            // starts and flips.
            style={{
              borderRadius: 12, background: "#000", maxHeight: "55vh", objectFit: "cover",
              transform: facing === "user" ? "scaleX(-1)" : "none",
            }}
          />
          {phase === "recording" && (
            <span
              className="position-absolute badge bg-danger"
              style={{ top: 10, left: 10, fontSize: 14 }}
            >
              <i className="mdi mdi-record me-1" aria-hidden="true" />
              REC {fmt(seconds)} / {fmt(MAX_SECONDS)}
            </span>
          )}
          {canFlip && phase === "live" && (
            <button
              type="button"
              className="btn btn-light btn-sm position-absolute"
              style={{ top: 10, right: 10, borderRadius: 20 }}
              onClick={flipCamera}
              title="Switch camera"
            >
              <i className="mdi mdi-camera-flip" aria-hidden="true" /> Flip
            </button>
          )}
        </div>

        {/* The take, for review */}
        {phase === "review" && previewUrl && (
          <video
            src={previewUrl}
            controls
            playsInline
            className="w-100 mb-3"
            // Same cap as the live preview, so Retake / Upload stay in view.
            // Contain, not cover: this is the take itself, nothing cropped.
            style={{ borderRadius: 12, background: "#000", maxHeight: "55vh", objectFit: "contain" }}
          />
        )}

        {/* Controls per phase */}
        {supported && phase === "idle" && (
          <button type="button" className="btn btn-primary w-100" onClick={() => startCamera()}>
            <i className="mdi mdi-camera me-1" aria-hidden="true" /> Start Camera
          </button>
        )}

        {phase === "live" && (
          <button type="button" className="btn btn-danger w-100" onClick={startRecording}>
            <i className="mdi mdi-record me-1" aria-hidden="true" /> Record
          </button>
        )}

        {phase === "recording" && (
          <button type="button" className="btn btn-dark w-100" onClick={stopRecording}>
            <i className="mdi mdi-stop me-1" aria-hidden="true" /> Stop
          </button>
        )}

        {phase === "review" && (
          <div className="d-flex gap-2">
            <button
              type="button"
              className="btn btn-outline-secondary w-50"
              disabled={uploading}
              onClick={supported ? retake : () => { setBlob(null); setPhase("idle"); }}
            >
              <i className="mdi mdi-refresh me-1" aria-hidden="true" /> Retake
            </button>
            <button
              type="button"
              className="btn btn-primary w-50"
              disabled={!blob || uploading}
              onClick={uploadTake}
            >
              {uploading ? `Uploading… ${progress}%` : "Upload Video"}
            </button>
          </div>
        )}

        {phase === "done" && supported && (
          <button type="button" className="btn btn-outline-primary w-100" onClick={retake}>
            Record another
          </button>
        )}

        {/* Fallback: no in-browser recording available. The input is what
            opens the camera app, but a native file input reads "Choose file"
            -- so it is hidden behind a button that says what it does. */}
        {!supported && phase !== "review" && (
          <>
            <input
              ref={fileInputRef}
              type="file"
              accept="video/*"
              capture="environment"
              style={{ display: "none" }}
              onChange={onPickFile}
            />
            <button
              type="button"
              className="btn btn-danger w-100"
              onClick={() => fileInputRef.current?.click()}
            >
              <i className="mdi mdi-video me-1" aria-hidden="true" /> Record with Camera
            </button>
          </>
        )}

        {uploading && (
          <div className="progress mt-3" style={{ height: 6 }}>
            <div className="progress-bar" style={{ width: `${progress}%` }} />
          </div>
        )}

        {status && (
          <div
            className={`mt-3 alert ${status.type === "success" ? "alert-success" : "alert-danger"} mb-0`}
          >
            {status.msg}
          </div>
        )}
      </div>
    </div>
  );
}
